import { atom } from 'jotai';
import { atomEffect } from 'jotai-effect';
import {
  Contract,
  MethodInput,
  ProposalCategory,
  ContractMethod,
} from '@/app/types/proposals';
import { Action } from '@/app/types/proposals';
import { getMethodInputs, generateId } from './aux';

export const proposalTypeDataAtom = atom<ProposalCategory | null>(null);

export const targetAddressAtom = atom<Contract | null>(null);
export const targetAddressWritableAtom = atom(
  (get) => {
    const proposalTypeData = get(proposalTypeDataAtom);
    const targetAddress = get(targetAddressAtom);

    const targetAddressValue = targetAddress || proposalTypeData?.contracts[0];

    return targetAddressValue;
  },
  (_get, set, newValue: Contract | null) => {
    set(targetAddressAtom, newValue);
    set(selectedMethodAtom, newValue?.methods[0] || null);
    set(methodInputsAtom, newValue?.methods[0]?.inputs || null);
  },
);

export const selectedMethodAtom = atom<ContractMethod | null>(null);
export const selectedMethodWritableAtom = atom(
  (get) => {
    // Here I would need to set an atom
    get(methodInputsEffect);
    return get(selectedMethodAtom);
  },
  (get, set, newValue: ContractMethod | null) => {
    set(selectedMethodAtom, newValue);
    set(methodInputsAtom, newValue?.inputs || null);
  },
);

export const methodInputsAtom = atom<MethodInput[] | null>(null);

const methodInputsEffect = atomEffect((get, set) => {
  const selectedMethod = get(selectedMethodAtom);
  const actionId = get(actionIdAtom);

  if (!actionId) set(methodInputsAtom, selectedMethod?.inputs || null);
});

export const methodInputsWritableAtom = atom(
  (get) => {
    const targetAddress = get(targetAddressAtom);
    const selectedMethod = get(selectedMethodAtom);
    const actionId = get(actionIdAtom);

    // if there's an action with the same target and method, return the inputs from the action,
    // because we are updating the input of an existing action
    if (actionId) {
      const action = get(actionsAtom).find((a) => a.id === actionId);
      return action?.inputs;
    } else {
      const proposalTypeData = get(proposalTypeDataAtom);

      if (!proposalTypeData) return null;

      const defaultContract = proposalTypeData?.contracts[0];

      // This because of the derived atom bug will fix later [TODO]
      const contract = targetAddress || defaultContract;
      const method = selectedMethod || contract?.methods[0];

      return method?.inputs;
    }
  },
  (_get, set, newValue: MethodInput[] | null) => {
    // transform input (that will be displayed to internalInput: the actualy value that will be sent in the transaction)
    const transformedInput = set(methodInputsAtom, newValue);
  },
);

export const actionsAtom = atom<Action[]>([]);
export const actionIdAtom = atom<string | null>(null);
export const actionsWritableAtom = atom(
  (get) => {
    const actions = get(actionsAtom);
    return actions;
  },
  (_get, set, newValue: Action[]) => {
    set(actionsAtom, newValue);
  },
);
export const newActionAtom = atom(null, (get, set) => {
  const data = get(proposalTypeDataAtom);

  set(targetAddressAtom, data?.contracts[0] || null);
  set(selectedMethodAtom, data?.contracts[0]?.methods[0] || null);
  set(methodInputsAtom, data?.contracts[0]?.methods[0]?.inputs || null);
});

export const updateActionsAtom = atom(
  null,
  (get, set, methodInputsList: MethodInput[]) => {
    const actions = get(actionsAtom);
    const targetAddress = get(targetAddressAtom);
    const selectedMethod = get(selectedMethodAtom);
    const actionId = get(actionIdAtom);

    const newAction: Action = {
      id: actionId || generateId(),
      target: targetAddress!.address as `0x${string}`,
      method: selectedMethod!.name,
      show: selectedMethod!.show,
      inputs: methodInputsList,
    };

    const existingActionIndex = actions.findIndex((action) => action.id === newAction.id);

    if (existingActionIndex !== -1) {
      const updatedActions = [...actions];
      updatedActions[existingActionIndex] = newAction;
      set(actionsWritableAtom, updatedActions);
    } else {
      set(actionsWritableAtom, [...actions, newAction]);
    }

    set(actionIdAtom, null);
  },
);

export const deleteActionAtom = atom(null, (get, set, actionToDelete: Action) => {
  const actions = get(actionsAtom);
  const filteredActions = actions.filter((existing) => existing.id !== actionToDelete.id);

  set(actionsAtom, filteredActions);
});

export const populateFormFromActionAtom = atom(null, (get, set, action: Action) => {
  const methodInputs = getMethodInputs(action.method);

  const data = get(proposalTypeDataAtom);
  const targetAddress = data?.contracts.find((c) => c.address === action.target);
  set(targetAddressAtom, targetAddress || null);
  set(
    selectedMethodAtom,
    targetAddress?.methods.find((m) => m.name === action.method) || null,
  );

  set(actionIdAtom, action.id);

  if (methodInputs) {
    const populatedInputs = methodInputs.map((input) => {
      const actionInput = action.inputs.find((ai) => ai.name === input.name);
      return {
        ...input,
        value: actionInput ? actionInput.value : '',
      };
    });
    set(methodInputsAtom, populatedInputs);
  }
});

targetAddressAtom.debugLabel = 'target_address';
targetAddressWritableAtom.debugLabel = 'target_address_writable';
selectedMethodAtom.debugLabel = 'selected_method';
selectedMethodWritableAtom.debugLabel = 'selected_method_writable';
methodInputsAtom.debugLabel = 'method_inputs';
methodInputsWritableAtom.debugLabel = 'method_inputs_writable';
